import {
  Box,
  Button,
  CloseButton,
  Flex,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Text,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { FaPen, FaPlus, FaSearch, FaSlidersH, FaTrash } from "react-icons/fa";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import SearchableSelect from "../../../components/SearchableSelect";
import ProspectTasksPanel from "./ProspectTasksPanel";
import ProspectQuotesPanel from "./ProspectQuotesPanel";
import { Lead, LeadComment, LeadVisit, ProspectStatus } from "../../../models/Lead";
import { getCnpjData } from "../../../services/cnpjService";
import { getLeadById, saveLead, updateLead } from "../../../services/crmService";
import { getUsers } from "../../../services/userService";
import { crm, CrmAvatar, Pill } from "./crmTheme";

interface UserOption {
  id: string;
  firstname?: string;
  lastname?: string;
  email?: string;
}

const userLabel = (u: UserOption) =>
  `${u.firstname ?? ""} ${u.lastname ?? ""}`.trim() || u.email || "Usuário";

const formatCep = (value: string) =>
  value.replace(/\D/g, "").replace(/^(\d{5})(\d{3}).*/, "$1-$2");

const formatCnpj = (value: string) =>
  value
    .replace(/\D/g, "")
    .replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/, "$1.$2.$3/$4-$5");

const emptyLead: Lead = {
  name: "",
  email: "",
  phone: "",
  cnpj: "",
  tradeName: "",
  foundingDate: "",
  street: "",
  addressNumber: "",
  city: "",
  state: "",
  zipCode: "",
  country: "",
  comments: [],
  visits: [],
};

// Section block helper for structured form layout
const SectionBlock = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Box
    bg={crm.subtle}
    border={`1px solid ${crm.border}`}
    borderRadius="10px"
    p="16px"
    mb="16px"
  >
    <Text fontSize="sm" fontWeight="700" color={crm.text} mb="12px">
      {title}
    </Text>
    {children}
  </Box>
);

// Styled form label
const FLabel = ({ children }: { children: React.ReactNode }) => (
  <FormLabel fontSize="xs" fontWeight="600" color={crm.textMuted} mb="4px">
    {children}
  </FormLabel>
);

// Input style props
const inputStyle = {
  h: "42px",
  borderColor: crm.border,
  borderRadius: "10px",
  _focus: { borderColor: crm.brand, boxShadow: `0 0 0 1px ${crm.brand}` },
  fontSize: "sm",
};

const Field = ({ label, value }: { label: string; value?: string | null }) => (
  <Box>
    <Text fontSize="xs" fontWeight="600" color={crm.textMuted} mb="2px">
      {label}
    </Text>
    <Text fontSize="sm" color={crm.text}>{value || "—"}</Text>
  </Box>
);

const LeadFormModal = ({
  isOpen,
  onClose,
  onSaved,
  statuses,
  leadId,
  defaultStatusId,
  onManageStatus,
  onDelete,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  statuses: ProspectStatus[];
  leadId?: string;
  defaultStatusId?: string;
  onManageStatus?: () => void;
  onDelete?: (lead: Lead) => void;
}) => {
  const toast = useToast();
  const auth = useAuthUser<any>();
  const isAdmin = !!auth?.admin;
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [form, setForm] = useState<Lead>({ ...emptyLead });
  const [mode, setMode] = useState<"view" | "edit">("view");

  // busca por CNPJ
  const [cnpjSearch, setCnpjSearch] = useState("");
  const [cnpjLoading, setCnpjLoading] = useState(false);

  // estado do rascunho de comentário / visita
  const [commentText, setCommentText] = useState("");
  // muda a cada visita adicionada para remontar o multiselect de acompanhantes
  // (react-select guarda estado interno e não limpa sozinho).
  const [visitKey, setVisitKey] = useState(0);
  const [visitDraft, setVisitDraft] = useState<LeadVisit>({
    scheduledDate: "",
    responsibleId: "",
    notes: "",
    participantIds: [],
  });

  const set = (patch: Partial<Lead>) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    getUsers().then((data) => setUsers(data ?? []));
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    if (leadId) {
      setMode("view");
      getLeadById(leadId).then((lead) => {
        if (lead)
          setForm({
            ...emptyLead,
            ...lead,
            comments: lead.comments ?? [],
            visits: lead.visits ?? [],
          });
      });
    } else {
      setMode("edit");
      setForm({ ...emptyLead, prospectStatusId: defaultStatusId });
    }
    setCommentText("");
    setCnpjSearch("");
    setVisitDraft({
      scheduledDate: "",
      responsibleId: "",
      notes: "",
      participantIds: [],
    });
  }, [isOpen, leadId, defaultStatusId]);

  // Reaproveita a busca pública por CNPJ (BrasilAPI) para autopreencher o lead.
  const fillFromCnpj = async () => {
    setCnpjLoading(true);
    const result = await getCnpjData(cnpjSearch);
    setCnpjLoading(false);

    if (result.error || !result.data) {
      toast({
        title: result.error ?? "Não foi possível consultar o CNPJ.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const d = result.data;
    set({
      name: d.razao_social ?? form.name,
      cnpj: d.cnpj ? formatCnpj(d.cnpj) : formatCnpj(cnpjSearch),
      tradeName: d.nome_fantasia ?? "",
      foundingDate: d.data_inicio_atividade
        ? DateTime.fromISO(d.data_inicio_atividade).toISODate() ?? ""
        : "",
      phone: d.ddd_telefone_1 ?? form.phone,
      street: d.logradouro ?? "",
      addressNumber: d.numero ?? "",
      city: d.municipio ?? "",
      state: d.uf ?? "",
      zipCode: d.cep ? formatCep(d.cep) : "",
      country: "Brasil",
    });

    toast({
      title: "Dados preenchidos a partir do CNPJ.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const addComment = () => {
    if (!commentText.trim()) return;
    const comment: LeadComment = { text: commentText.trim() };
    set({ comments: [...(form.comments ?? []), comment] });
    setCommentText("");
  };

  const removeComment = (idx: number) => {
    set({ comments: (form.comments ?? []).filter((_, i) => i !== idx) });
  };

  const addVisit = () => {
    if (!visitDraft.scheduledDate) {
      toast({ title: "Informe a data da visita.", status: "warning" });
      return;
    }
    set({ visits: [...(form.visits ?? []), visitDraft] });
    setVisitDraft({
      scheduledDate: "",
      responsibleId: "",
      notes: "",
      participantIds: [],
    });
    setVisitKey((k) => k + 1);
  };

  const removeVisit = (idx: number) => {
    set({ visits: (form.visits ?? []).filter((_, i) => i !== idx) });
  };

  const submit = async () => {
    if (!form.name?.trim()) {
      toast({ title: "Informe o nome do lead.", status: "warning" });
      return;
    }
    setLoading(true);
    try {
      // Datas precisam ir em ISO 8601 completo (com segundos); o System.Text.Json
      // do backend não aceita date-only nem datetime sem segundos. Preservamos o
      // horário "de parede" (sem converter para UTC) para não deslocar o fuso.
      const toIso = (value?: string) => {
        if (!value) return undefined;
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T00:00:00`;
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return `${value}:00`;
        return value;
      };

      const payload: Lead = {
        ...form,
        // Status legado (string) é obrigatório no backend; a etapa do funil real é o prospectStatusId.
        status: form.status || "Pendente",
        responsibleId: form.responsibleId || undefined,
        prospectStatusId: form.prospectStatusId || undefined,
        foundingDate: toIso(form.foundingDate),
        visits: (form.visits ?? []).map((v) => ({
          ...v,
          scheduledDate: toIso(v.scheduledDate) ?? v.scheduledDate,
          responsibleId: v.responsibleId || undefined,
        })),
      };
      const response = leadId
        ? await updateLead(payload)
        : await saveLead(payload);

      if (response && (response.status === 200 || response.data?.success)) {
        toast({
          title: leadId ? "Lead atualizado." : "Lead criado.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onSaved();
        onClose();
      } else {
        toast({
          title: response?.data?.notifications?.[0] ?? "Erro ao salvar lead.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (e) {
      toast({ title: "Erro ao salvar lead.", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const foundingDateDisplay = form.foundingDate
    ? DateTime.fromISO(form.foundingDate).toFormat("dd/MM/yyyy")
    : undefined;

  const statusDisplay =
    form.prospectStatusName ||
    statuses.find((s) => s.id === form.prospectStatusId)?.name ||
    "Sem status";

  const statusColor =
    statuses.find((s) => s.id === form.prospectStatusId)?.color;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={leadId ? "6xl" : "3xl"}
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
      <ModalContent
        maxW={leadId ? "1400px" : undefined}
        borderRadius="16px"
        boxShadow={crm.shadowLg}
      >
        <ModalHeader borderBottom={`1px solid ${crm.border}`}>
          <Flex justify="space-between" align="center">
            <Text fontWeight="700" fontSize="md" color={crm.text}>
              {mode === "view"
                ? form.name || "Lead"
                : leadId
                ? "Editar lead"
                : "Cadastrar novo lead"}
            </Text>
            <CloseButton onClick={onClose} />
          </Flex>
        </ModalHeader>
        <ModalBody>
          {/* ── VIEW MODE ── */}
          {mode === "view" && (
            <Flex align="stretch" gap="0">
              <Box flex="1" minW="0">
                <SectionBlock title="Dados do cliente">
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px">
                    <Field label="Nome" value={form.name} />
                    <Field label="E-mail" value={form.email} />
                    <Field label="Telefone" value={form.phone} />
                    <Box>
                      <Text fontSize="xs" fontWeight="600" color={crm.textMuted} mb="2px">
                        Responsável
                      </Text>
                      {form.responsibleName ? (
                        <Flex align="center" gap="6px">
                          <CrmAvatar name={form.responsibleName} size={20} />
                          <Text fontSize="sm" color={crm.text}>{form.responsibleName}</Text>
                        </Flex>
                      ) : (
                        <Text fontSize="sm" color={crm.text}>—</Text>
                      )}
                    </Box>
                    <Field label="CNPJ" value={form.cnpj} />
                    <Field label="Nome fantasia" value={form.tradeName} />
                    <Field label="Data de fundação" value={foundingDateDisplay} />
                  </SimpleGrid>
                </SectionBlock>

                <SectionBlock title="Endereço">
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing="12px">
                    <Field label="CEP" value={form.zipCode} />
                    <Field label="Rua" value={form.street} />
                    <Field label="Número" value={form.addressNumber} />
                    <Field label="Cidade" value={form.city} />
                    <Field label="Estado" value={form.state} />
                    <Field label="País" value={form.country} />
                  </SimpleGrid>
                </SectionBlock>

                <SectionBlock title="Status (etapa do funil)">
                  <Pill
                    bg={statusColor ? `${statusColor}22` : crm.subtle}
                    color={statusColor || crm.textMuted}
                    borderColor={statusColor}
                  >
                    {statusDisplay}
                  </Pill>
                </SectionBlock>

                <SectionBlock title="Comentários">
                  {(form.comments ?? []).length === 0 ? (
                    <Text fontSize="sm" color={crm.textFaint}>
                      Sem comentários.
                    </Text>
                  ) : (
                    <VStack align="stretch" spacing="8px">
                      {(form.comments ?? []).map((c, idx) => (
                        <Flex
                          key={c.id ?? `c-${idx}`}
                          p="10px"
                          bg={crm.subtle}
                          border={`1px solid ${crm.border}`}
                          borderLeft={`3px solid ${crm.brand}`}
                          borderRadius="8px"
                          gap="8px"
                          align="flex-start"
                        >
                          {c.authorName && <CrmAvatar name={c.authorName} size={24} />}
                          <Box>
                            <Text fontSize="sm" color={crm.text}>{c.text}</Text>
                            {(c.authorName || c.createdDate) && (
                              <Text fontSize="xs" color={crm.textMuted} mt="2px">
                                {[
                                  c.authorName,
                                  c.createdDate
                                    ? new Date(c.createdDate).toLocaleDateString("pt-BR")
                                    : null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </Text>
                            )}
                          </Box>
                        </Flex>
                      ))}
                    </VStack>
                  )}
                </SectionBlock>

                <SectionBlock title="Visitas agendadas">
                  {(form.visits ?? []).length === 0 ? (
                    <Text fontSize="sm" color={crm.textFaint}>
                      Sem visitas agendadas.
                    </Text>
                  ) : (
                    <VStack align="stretch" spacing="8px">
                      {(form.visits ?? []).map((v, idx) => (
                        <Flex
                          key={v.id ?? `v-${idx}`}
                          p="10px"
                          bg={crm.subtle}
                          border={`1px solid ${crm.border}`}
                          borderLeft={`3px solid ${crm.brand}`}
                          borderRadius="8px"
                        >
                          <Box>
                            <Text fontSize="sm" fontWeight="600" color={crm.text}>
                              {v.scheduledDate
                                ? new Date(v.scheduledDate).toLocaleString("pt-BR")
                                : "-"}
                            </Text>
                            <Text fontSize="xs" color={crm.textMuted}>
                              {v.responsibleName ? `Resp.: ${v.responsibleName}` : ""}
                              {v.participantIds?.length
                                ? ` · ${v.participantIds.length} acompanhante(s)`
                                : ""}
                              {v.notes ? ` · ${v.notes}` : ""}
                            </Text>
                          </Box>
                        </Flex>
                      ))}
                    </VStack>
                  )}
                </SectionBlock>
                {leadId && <ProspectQuotesPanel prospectId={leadId} />}
              </Box>
              {leadId && (
                <ProspectTasksPanel prospectId={leadId} isAdmin={isAdmin} />
              )}
            </Flex>
          )}

          {/* ── EDIT MODE ── */}
          {mode === "edit" && (
            <Flex align="stretch" gap="0">
              <Box flex="1" minW="0">
                {/* Busca por CNPJ — autopreenche nome, dados fiscais e endereço */}
                <Box
                  p="14px"
                  bg={crm.brandSoft}
                  border={`1px solid ${crm.brand}33`}
                  borderRadius="10px"
                  mb="16px"
                >
                  <FormLabel fontSize="sm" fontWeight="600" color={crm.text} mb="8px">
                    Buscar dados da empresa pelo CNPJ
                  </FormLabel>
                  <Flex gap="8px">
                    <Input
                      bg={crm.surface}
                      placeholder="Digite o CNPJ e clique em buscar"
                      value={cnpjSearch}
                      onChange={(e) => setCnpjSearch(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && fillFromCnpj()}
                      {...inputStyle}
                    />
                    <Button
                      leftIcon={<FaSearch />}
                      bg={crm.brand}
                      color="white"
                      _hover={{ bg: crm.brandDark }}
                      borderRadius="10px"
                      minW="120px"
                      isLoading={cnpjLoading}
                      onClick={fillFromCnpj}
                    >
                      Buscar
                    </Button>
                  </Flex>
                </Box>

                {/* Identificação / contatos */}
                <SectionBlock title="Dados do cliente">
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px">
                    <Box>
                      <FLabel>Nome *</FLabel>
                      <Input
                        value={form.name ?? ""}
                        onChange={(e) => set({ name: e.target.value })}
                        placeholder="Nome do lead / empresa"
                        {...inputStyle}
                      />
                    </Box>
                    <Box>
                      <FLabel>E-mail</FLabel>
                      <Input
                        type="email"
                        value={form.email ?? ""}
                        onChange={(e) => set({ email: e.target.value })}
                        placeholder="email@exemplo.com"
                        {...inputStyle}
                      />
                    </Box>
                    <Box>
                      <FLabel>Telefone</FLabel>
                      <Input
                        value={form.phone ?? ""}
                        onChange={(e) => set({ phone: e.target.value })}
                        placeholder="(00) 00000-0000"
                        {...inputStyle}
                      />
                    </Box>
                    <Box>
                      <FLabel>Responsável pelo lead</FLabel>
                      <Select
                        placeholder="Selecione um responsável"
                        value={form.responsibleId ?? ""}
                        onChange={(e) => set({ responsibleId: e.target.value })}
                        h="42px"
                        borderColor={crm.border}
                        borderRadius="10px"
                        _focus={{ borderColor: crm.brand, boxShadow: `0 0 0 1px ${crm.brand}` }}
                        fontSize="sm"
                      >
                        {users.map((u) => (
                          <option key={u.id} value={u.id}>
                            {userLabel(u)}
                          </option>
                        ))}
                      </Select>
                    </Box>
                    <Box>
                      <FLabel>CNPJ</FLabel>
                      <Input
                        value={form.cnpj ?? ""}
                        onChange={(e) => set({ cnpj: e.target.value })}
                        placeholder="00.000.000/0000-00"
                        {...inputStyle}
                      />
                    </Box>
                    <Box>
                      <FLabel>Nome fantasia</FLabel>
                      <Input
                        value={form.tradeName ?? ""}
                        onChange={(e) => set({ tradeName: e.target.value })}
                        {...inputStyle}
                      />
                    </Box>
                    <Box>
                      <FLabel>Data de fundação</FLabel>
                      <Input
                        type="date"
                        value={form.foundingDate ? form.foundingDate.substring(0, 10) : ""}
                        onChange={(e) => set({ foundingDate: e.target.value })}
                        {...inputStyle}
                      />
                    </Box>
                  </SimpleGrid>
                </SectionBlock>

                {/* Endereço */}
                <SectionBlock title="Endereço">
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing="12px">
                    <Box>
                      <FLabel>CEP</FLabel>
                      <Input
                        value={form.zipCode ?? ""}
                        onChange={(e) => set({ zipCode: e.target.value })}
                        {...inputStyle}
                      />
                    </Box>
                    <Box gridColumn={{ md: "span 2" }}>
                      <FLabel>Rua</FLabel>
                      <Input
                        value={form.street ?? ""}
                        onChange={(e) => set({ street: e.target.value })}
                        {...inputStyle}
                      />
                    </Box>
                    <Box>
                      <FLabel>Número</FLabel>
                      <Input
                        value={form.addressNumber ?? ""}
                        onChange={(e) => set({ addressNumber: e.target.value })}
                        {...inputStyle}
                      />
                    </Box>
                    <Box>
                      <FLabel>Cidade</FLabel>
                      <Input
                        value={form.city ?? ""}
                        onChange={(e) => set({ city: e.target.value })}
                        {...inputStyle}
                      />
                    </Box>
                    <Box>
                      <FLabel>Estado</FLabel>
                      <Input
                        value={form.state ?? ""}
                        onChange={(e) => set({ state: e.target.value })}
                        {...inputStyle}
                      />
                    </Box>
                    <Box>
                      <FLabel>País</FLabel>
                      <Input
                        value={form.country ?? ""}
                        onChange={(e) => set({ country: e.target.value })}
                        {...inputStyle}
                      />
                    </Box>
                  </SimpleGrid>
                </SectionBlock>

                {/* Status (coluna do funil) */}
                <SectionBlock title="Status (etapa do funil)">
                  <Flex align="center" justify="space-between" mb="10px">
                    {onManageStatus && (
                      <Button
                        size="xs"
                        variant="outline"
                        borderColor={crm.border}
                        color={crm.textMuted}
                        _hover={{ bg: crm.brandSoft, borderColor: crm.brand }}
                        borderRadius="8px"
                        leftIcon={<FaSlidersH />}
                        onClick={onManageStatus}
                      >
                        Gerenciar status
                      </Button>
                    )}
                  </Flex>
                  {statuses.length === 0 ? (
                    <Flex
                      align="center"
                      gap="12px"
                      p="12px"
                      borderWidth="1px"
                      borderRadius="md"
                      bg={crm.subtle}
                      flexWrap="wrap"
                    >
                      <Text fontSize="sm" color={crm.textMuted}>
                        Nenhum status cadastrado.
                      </Text>
                      {onManageStatus && (
                        <Button
                          size="sm"
                          bg={crm.brand}
                          color="white"
                          _hover={{ bg: crm.brandDark }}
                          borderRadius="8px"
                          leftIcon={<FaPlus />}
                          onClick={onManageStatus}
                        >
                          Criar status
                        </Button>
                      )}
                    </Flex>
                  ) : (
                    <Select
                      placeholder="Sem status"
                      value={form.prospectStatusId ?? ""}
                      onChange={(e) => set({ prospectStatusId: e.target.value })}
                      maxW="320px"
                      h="42px"
                      borderColor={crm.border}
                      borderRadius="10px"
                      _focus={{ borderColor: crm.brand, boxShadow: `0 0 0 1px ${crm.brand}` }}
                      fontSize="sm"
                    >
                      {statuses.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </SectionBlock>

                {/* Comentários */}
                <SectionBlock title="Comentários">
                  <Flex gap="8px" mb="10px">
                    <Textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Adicionar um comentário..."
                      rows={2}
                      borderColor={crm.border}
                      borderRadius="10px"
                      _focus={{ borderColor: crm.brand, boxShadow: `0 0 0 1px ${crm.brand}` }}
                      fontSize="sm"
                    />
                    <IconButton
                      aria-label="Adicionar comentário"
                      icon={<FaPlus />}
                      onClick={addComment}
                      bg={crm.brand}
                      color="white"
                      _hover={{ bg: crm.brandDark }}
                      borderRadius="10px"
                    />
                  </Flex>
                  <VStack align="stretch" spacing="8px">
                    {(form.comments ?? []).map((c, idx) => (
                      <Flex
                        key={c.id ?? `new-${idx}`}
                        justify="space-between"
                        align="flex-start"
                        p="10px"
                        bg={crm.subtle}
                        border={`1px solid ${crm.border}`}
                        borderLeft={`3px solid ${crm.brand}`}
                        borderRadius="8px"
                        gap="8px"
                      >
                        {c.authorName && <CrmAvatar name={c.authorName} size={24} />}
                        <Box flex={1}>
                          <Text fontSize="sm" color={crm.text}>{c.text}</Text>
                          {(c.authorName || c.createdDate) && (
                            <Text fontSize="xs" color={crm.textMuted} mt="2px">
                              {[
                                c.authorName,
                                c.createdDate
                                  ? new Date(c.createdDate).toLocaleDateString("pt-BR")
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </Text>
                          )}
                        </Box>
                        {!c.id && (
                          <IconButton
                            aria-label="Remover"
                            icon={<FaTrash />}
                            size="xs"
                            variant="ghost"
                            color={crm.textMuted}
                            _hover={{ color: crm.danger }}
                            onClick={() => removeComment(idx)}
                          />
                        )}
                      </Flex>
                    ))}
                  </VStack>
                </SectionBlock>

                {/* Visitas agendadas */}
                <SectionBlock title="Visitas agendadas">
                  <Box
                    p="12px"
                    border={`1px solid ${crm.border}`}
                    borderRadius="10px"
                    mb="12px"
                    bg={crm.surface}
                  >
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="10px">
                      <Box>
                        <FLabel>Data e hora</FLabel>
                        <Input
                          type="datetime-local"
                          value={visitDraft.scheduledDate}
                          onChange={(e) =>
                            setVisitDraft((v) => ({
                              ...v,
                              scheduledDate: e.target.value,
                            }))
                          }
                          {...inputStyle}
                        />
                      </Box>
                      <Box>
                        <FLabel>Responsável</FLabel>
                        <Select
                          placeholder="Selecione"
                          value={visitDraft.responsibleId ?? ""}
                          onChange={(e) =>
                            setVisitDraft((v) => ({
                              ...v,
                              responsibleId: e.target.value,
                            }))
                          }
                          h="42px"
                          borderColor={crm.border}
                          borderRadius="10px"
                          _focus={{ borderColor: crm.brand, boxShadow: `0 0 0 1px ${crm.brand}` }}
                          fontSize="sm"
                        >
                          {users.map((u) => (
                            <option key={u.id} value={u.id}>
                              {userLabel(u)}
                            </option>
                          ))}
                        </Select>
                      </Box>
                      <Box gridColumn={{ md: "span 2" }}>
                        <FLabel>Quem vai junto</FLabel>
                        <SearchableSelect
                          key={visitKey}
                          isMulti
                          placeholder="Adicionar acompanhantes"
                          options={users.map((u) => ({
                            value: u.id,
                            label: userLabel(u),
                          }))}
                          onMultiChange={(vals) =>
                            setVisitDraft((v) => ({
                              ...v,
                              participantIds: vals.map((x) => x.value),
                            }))
                          }
                        />
                      </Box>
                      <Box gridColumn={{ md: "span 2" }}>
                        <FLabel>Observações</FLabel>
                        <Input
                          value={visitDraft.notes ?? ""}
                          onChange={(e) =>
                            setVisitDraft((v) => ({ ...v, notes: e.target.value }))
                          }
                          {...inputStyle}
                        />
                      </Box>
                    </SimpleGrid>
                    <Flex justify="flex-end" mt="10px">
                      <Button
                        leftIcon={<FaPlus />}
                        size="sm"
                        onClick={addVisit}
                        variant="outline"
                        borderColor={crm.brand}
                        color={crm.brand}
                        _hover={{ bg: crm.brandSoft }}
                        borderRadius="8px"
                      >
                        Adicionar visita
                      </Button>
                    </Flex>
                  </Box>

                  <VStack align="stretch" spacing="8px">
                    {(form.visits ?? []).map((v, idx) => (
                      <Flex
                        key={v.id ?? `new-${idx}`}
                        justify="space-between"
                        align="center"
                        p="10px"
                        bg={crm.subtle}
                        border={`1px solid ${crm.border}`}
                        borderLeft={`3px solid ${crm.brand}`}
                        borderRadius="8px"
                      >
                        <Box>
                          <Text fontSize="sm" fontWeight="600" color={crm.text}>
                            {v.scheduledDate
                              ? new Date(v.scheduledDate).toLocaleString("pt-BR")
                              : "-"}
                          </Text>
                          <Text fontSize="xs" color={crm.textMuted}>
                            {v.responsibleName ? `Resp.: ${v.responsibleName}` : ""}
                            {v.participantIds?.length
                              ? ` · ${v.participantIds.length} acompanhante(s)`
                              : ""}
                            {v.notes ? ` · ${v.notes}` : ""}
                          </Text>
                        </Box>
                        {!v.id && (
                          <IconButton
                            aria-label="Remover"
                            icon={<FaTrash />}
                            size="xs"
                            variant="ghost"
                            color={crm.textMuted}
                            _hover={{ color: crm.danger }}
                            onClick={() => removeVisit(idx)}
                          />
                        )}
                      </Flex>
                    ))}
                  </VStack>
                </SectionBlock>
                {leadId && <ProspectQuotesPanel prospectId={leadId} />}
              </Box>
              {leadId && (
                <ProspectTasksPanel prospectId={leadId} isAdmin={isAdmin} />
              )}
            </Flex>
          )}
        </ModalBody>
        <ModalFooter borderTop={`1px solid ${crm.border}`}>
          {mode === "view" ? (
            <>
              <Button mr="auto" onClick={onClose} variant="ghost" color={crm.textMuted}>
                Fechar
              </Button>
              <Button
                variant="outline"
                borderColor={crm.danger}
                color={crm.danger}
                _hover={{ bg: "#fef2f2" }}
                borderRadius="10px"
                leftIcon={<FaTrash />}
                mr="12px"
                onClick={() => onDelete?.(form)}
              >
                Excluir
              </Button>
              <Button
                bg={crm.brand}
                color="white"
                _hover={{ bg: crm.brandDark }}
                borderRadius="10px"
                leftIcon={<FaPen />}
                onClick={() => setMode("edit")}
              >
                Editar
              </Button>
            </>
          ) : (
            <>
              <Button
                mr="12px"
                onClick={leadId ? () => setMode("view") : onClose}
                variant="ghost"
                color={crm.textMuted}
              >
                Cancelar
              </Button>
              <Button
                bg={crm.brand}
                color="white"
                _hover={{ bg: crm.brandDark }}
                borderRadius="10px"
                onClick={submit}
                isLoading={loading}
              >
                {leadId ? "Salvar" : "Criar lead"}
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LeadFormModal;
